import { jwtDecode } from 'jwt-decode'

import { InspectorRootNodes, noDataTag } from '@/plugins/devtools/shared'

import type { Devtools } from '@/plugins/devtools/api'
import type { CustomInspectorNode, CustomInspectorState } from '@vue/devtools-kit'
import type { JwtPayload } from 'jwt-decode'

const rootNodeTitle = 'Session Data'

interface PFJwtPayload extends JwtPayload {
  exp: number
  jti: string
  token_type: string
  user_id: number
  is_staff: boolean
  is_superuser: boolean
  on_behalf_of: unknown
  service_name: string
  user_ulid: string
  environment_id: string
  user_version: number
}

function processToken (token?: string | null): CustomInspectorState[string] {
  const decoded = (() => {
    try {
      if (!token) return null
      return jwtDecode<PFJwtPayload>(token)
    } catch {
      return null
    }
  })()
  if (!decoded) {
    return [
      {
        key: 'token',
        editable: false,
        value: '(No Token)',
      },
    ]
  }

  return [
    {
      key: 'token',
      editable: false,
      value: token,
    },
    {
      key: 'expiration',
      editable: false,
      value: secondsToDate(decoded.exp),
    },
    {
      key: 'jti',
      editable: false,
      value: decoded.jti,
    },
    {
      key: 'token_type',
      editable: false,
      value: decoded.token_type,
    },
    {
      key: 'user_id',
      editable: false,
      value: decoded.user_id,
    },
    {
      key: 'is_staff',
      editable: false,
      value: decoded.is_staff,
    },
    {
      key: 'is_superuser',
      editable: false,
      value: decoded.is_superuser,
    },
    {
      key: 'service_name',
      editable: false,
      value: decoded.service_name,
    },
    {
      key: 'user_ulid',
      editable: false,
      value: decoded.user_ulid,
    },
    {
      key: 'environment_id',
      editable: false,
      value: decoded.environment_id,
    },
    {
      key: 'user_version',
      editable: false,
      value: decoded.user_version,
    },
  ]
}

function secondsToDate (seconds: number | undefined): string | undefined {
  return seconds
    ? new Date(seconds * 1000).toISOString()
    : undefined
}

export function setupSessionNode (devtools: Devtools): void {
  devtools.rootNodeGetters.push((payload) => {
    if (payload.filter && !rootNodeTitle.toLowerCase().includes(payload.filter.toLowerCase())) {
      return null
    }

    const node: Required<CustomInspectorNode> = {
      id: InspectorRootNodes.SESSION,
      label: rootNodeTitle,
      children: [],
      tags: [],
      name: '',
      file: '',
    }
    if (!devtools.stores.api?.refreshToken && !devtools.stores.api?.spaToken) {
      node.tags.push(noDataTag)
    }
    return node
  })

  devtools.stateGetters.push((payload) => {
    if (devtools.getRootNode(payload.nodeId) !== InspectorRootNodes.SESSION) return

    const apiStore = devtools.stores.api

    payload.state = {
      refresh: processToken(apiStore?.refreshToken),
      access: processToken(apiStore?.accessToken),
      spa: apiStore?.spaToken
        ? [
            {
              key: 'token',
              editable: false,
              value: apiStore.spaToken,
            },
            {
              key: 'expiration',
              editable: false,
              value: apiStore.spaTokenExpiration,
            },
          ]
        : [
            {
              key: 'token',
              editable: false,
              value: '(No Token)',
            },
          ],
    }
  })
}
